import { useState, useEffect } from 'react';
import { Plus, Trash2, Bell, BellOff, Search, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../utils/authFetch';
import { toast } from 'sonner';
import PageHelp from '../../components/PageHelp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MONO = { fontFamily: "'IBM Plex Mono', monospace" };
const SERIF = { fontFamily: "'Bodoni Moda', Georgia, serif" };

const FILTER_FIELDS = [
  { key: 'role',             label: 'Role',         placeholder: 'e.g. Fashion Designer' },
  { key: 'seniority',        label: 'Seniority',    placeholder: 'e.g. Senior' },
  { key: 'location_city',   label: 'City',         placeholder: 'e.g. Milan' },
  { key: 'location_country',label: 'Country',      placeholder: 'e.g. Italy' },
  { key: 'availability',     label: 'Availability', placeholder: 'e.g. Available' },
];

const SavedSearches = () => {
  const navigate = useNavigate();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newSearch, setNewSearch] = useState({ name: '', notify_on_match: true, filters: {} });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    authFetch(`${BACKEND}/api/dashboard/saved-searches`, {})
      .then(r => r.json())
      .then(d => { if (d.success) setSearches(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newSearch.name.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND}/api/dashboard/saved-searches`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSearch),
      });
      if (!res.ok) throw new Error('Failed to save');
      setShowForm(false);
      setNewSearch({ name: '', notify_on_match: true, filters: {} });
      load();
      toast.success('Talent brief saved');
    } catch {
      toast.error('Could not save brief. Please try again.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await authFetch(`${BACKEND}/api/dashboard/saved-searches/${id}`, { method: 'DELETE' });
      setSearches(prev => prev.filter(s => s.id !== id));
      toast.success('Brief deleted');
    } catch {
      toast.error('Could not delete brief');
    } finally { setDeleting(null); }
  };

  const setFilter = (key, value) => setNewSearch(p => ({ ...p, filters: { ...p.filters, [key]: value } }));

  const browseMatches = (s) => {
    const role = s.filters?.role;
    const city = s.filters?.location_city;
    const params = new URLSearchParams();
    if (role) params.set('role', role);
    if (city) params.set('city', city);
    navigate(`/gallery?${params.toString()}`);
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="saved-searches-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 style={{ ...SERIF, fontSize: '2rem', lineHeight: 1.1 }} className="font-semibold text-foreground">Talent Briefs</h1>
          <p style={{ ...MONO, fontSize: '10px', letterSpacing: '0.12em' }} className="text-muted-foreground uppercase mt-1">
            Saved searches · match notifications
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowForm(!showForm)}
            className="rounded-none bg-foreground text-background hover:bg-foreground/80 gap-2 text-sm"
            data-testid="new-brief-btn">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> New Brief
          </Button>
          <PageHelp pageKey="searches" />
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white border border-border p-6" data-testid="new-brief-form">
          <h2 className="font-semibold text-sm mb-5">New Talent Brief</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">
                Brief Name
              </label>
              <Input value={newSearch.name} onChange={e => setNewSearch(p => ({ ...p, name: e.target.value }))} required
                placeholder="e.g. Senior Womenswear Designer — Milan" className="rounded-none h-9" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {FILTER_FIELDS.map(field => (
                <div key={field.key}>
                  <label style={{ ...MONO, fontSize: '9px', letterSpacing: '0.12em' }} className="block text-muted-foreground uppercase mb-1.5">
                    {field.label}
                  </label>
                  <Input value={newSearch.filters[field.key] || ''} onChange={e => setFilter(field.key, e.target.value)}
                    placeholder={field.placeholder} className="rounded-none h-9" />
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <div
                onClick={() => setNewSearch(p => ({ ...p, notify_on_match: !p.notify_on_match }))}
                className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center cursor-pointer transition-colors ${newSearch.notify_on_match ? 'bg-foreground border-foreground' : 'border-border'}`}
              >
                {newSearch.notify_on_match && <span className="text-background text-xs">✓</span>}
              </div>
              <span className="text-sm">Email me when new candidates match this brief</span>
            </label>
            <div className="flex gap-3 pt-1">
              <Button type="submit" disabled={saving} className="rounded-none bg-foreground text-background hover:bg-foreground/80 text-sm h-9">
                {saving ? 'Saving...' : 'Save Brief'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-none text-sm h-9">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-white border border-border animate-pulse" />
          ))}
        </div>
      ) : searches.length === 0 ? (
        /* Empty state */
        <div className="bg-white border border-border" data-testid="empty-briefs">
          <div className="py-16 px-8 text-center">
            <div className="w-10 h-10 border border-border flex items-center justify-center mx-auto mb-5">
              <Search className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <h2 style={SERIF} className="text-xl font-semibold mb-2">No talent briefs yet</h2>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-6 leading-relaxed">
              Create a brief to save your search criteria and get notified when new matching candidates join.
            </p>
            <Button onClick={() => setShowForm(true)}
              className="rounded-none bg-foreground text-background hover:bg-foreground/80 gap-2 text-sm">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> Create your first brief
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {searches.map(s => {
            const activeFilters = Object.entries(s.filters || {}).filter(([, v]) => v);
            return (
              <div key={s.id} className="bg-white border border-border hover:border-foreground/20 transition-colors" data-testid={`brief-card-${s.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Brief name + meta */}
                      <div className="flex items-center gap-2.5 mb-3 flex-wrap">
                        <h3 className="font-semibold text-sm">{s.name}</h3>
                        <div className={`flex items-center gap-1 ${s.notify_on_match ? 'text-blue-500' : 'text-muted-foreground/40'}`}>
                          {s.notify_on_match
                            ? <Bell className="w-3.5 h-3.5" strokeWidth={1.5} />
                            : <BellOff className="w-3.5 h-3.5" strokeWidth={1.5} />}
                        </div>
                        {(s.match_count || 0) > 0 && (
                          <span style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }}
                            className="bg-foreground text-background px-2 py-0.5 uppercase">
                            {s.match_count} matches
                          </span>
                        )}
                      </div>

                      {/* Filter chips */}
                      {activeFilters.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {activeFilters.map(([k, v]) => (
                            <span key={k} style={{ ...MONO, fontSize: '9px', letterSpacing: '0.06em' }}
                              className="border border-border text-muted-foreground px-2 py-0.5 uppercase">
                              {v}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Footer */}
                      <p style={{ ...MONO, fontSize: '9px', letterSpacing: '0.08em' }} className="text-muted-foreground/50">
                        Created {new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        onClick={() => browseMatches(s)}
                        variant="outline"
                        className="rounded-none text-xs h-8 gap-1 hidden sm:flex"
                        data-testid={`browse-matches-${s.id}`}>
                        Browse Matches <ArrowRight className="w-3 h-3" />
                      </Button>
                      <button
                        onClick={() => handleDelete(s.id)}
                        disabled={deleting === s.id}
                        className="text-muted-foreground/40 hover:text-red-500 transition-colors p-1"
                        data-testid={`delete-brief-${s.id}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SavedSearches;
